import React, { useState, useEffect,useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Header from "./MenuBar";
import Footer from "./Footer";
import { Button, TextField, Box, Grid, Typography, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import "./Style.css";
import { NotificationContext } from "../redux/NotificationProvider";
const envVariable = require("../EnvironmentVariables.json");

function secondsToHms(seconds) {
  const h = Math.floor(seconds / 3600)
    .toString()
    .padStart(2, "0");
  const m = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor((seconds % 3600) % 60)
    .toString()
    .padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function getElapsedSeconds(startDateTime) {
  const startedAt = parseUTCDateTime(startDateTime);
  if (!startedAt || isNaN(startedAt.getTime())) {
    return 0;
  }

  return Math.max(0, Math.floor((Date.now() - startedAt.getTime()) / 1000));
}

// Parse UTC datetime string from backend to local Date object
const parseUTCDateTime = (utcString) => {
  if (!utcString) return null;
  if (utcString instanceof Date) return utcString;
  // Append 'Z' to indicate UTC if not already present
  const s = utcString.includes('T') ? utcString : utcString.replace(' ', 'T');
  const withZ = s.endsWith('Z') ? s : s + 'Z';
  return new Date(withZ);
};

// Format Date object to local datetime string for input fields (YYYY-MM-DDTHH:mm)
const formatLocalDateTime = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : parseUTCDateTime(date);
  if (!d || isNaN(d.getTime())) return '';

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Format Date object for display (YYYY-MM-DD HH:mm) - with space instead of T
const formatLocalDateTimeDisplay = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : parseUTCDateTime(date);
  if (!d || isNaN(d.getTime())) return '';

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

// Format Date object to UTC string for backend (YYYY-MM-DD HH:mm:ss)
const formatUTCDateTime = (date) => {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;

  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  const seconds = String(d.getUTCSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const EditTask = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [taskData, setTaskData] = useState({
    serviceInformation: "",
    notes: "",
    faultyParts: "",
    timer: "",
    originDate: "",
    assignedDate: "",
    calendarStart: "", // Editable start time
    calendarEnd: "", // Editable end time
    expectedDuration: 0,
    mechanicId: "",
    originalMechanicId: "", // Track original to detect changes
    status: "",
    customerId: "",
    customerName: "",
    vinNumber: "",
    time: "",
    appointmentDate: "",
    appointmentTime: "",
    appointmentDateTime: "",
    vin_number: "",
    accumulatedSeconds: 0,
    timerStartedAt: "",
    isTimerActive: false,
  });
  const [mechanics, setMechanics] = useState([]);
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");
  const [availability, setAvailability] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  /* eslint-disable no-unused-vars -- Several of these were declared but unused -- should probably be finished */
  const [customerId, setCustomerId] = useState(location.state?.customerId ?? "");
  const [customerName, setCustomerName] = useState(location.state?.customerName ?? "");
  const [mechanicId, setMechanicId] = useState(location.state?.mechanicId ?? "");
  const [vin_number, setVinNumber] = useState(location.state?.vin_number ?? "");
  const [phoneError, setPhoneError] = useState(""); // Partially implemented, see below line 99
  /* eslint-enable no-unused-vars */

  // Vehicle selection and editing state
  const [customerVehicles, setCustomerVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [editVehicleOpen, setEditVehicleOpen] = useState(false);
  const [editVehicle, setEditVehicle] = useState({
    user_car_id: null,
    car_name: "",
    make: "",
    model: "",
    year: "",
    submodel: "",
    vin_number: "",
  });

  // Add Vehicle dialog state
  const [addVehicleOpen, setAddVehicleOpen] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    car_name: "",
    make: "",
    model: "",
    year: "",
    submodel: "",
    engine: "",
    vin_number: "",
  });

  // Customer info state
  const [customerInfo, setCustomerInfo] = useState({
    user_id: null,
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });
  const [isExistingCustomer, setIsExistingCustomer] = useState(false); // Track if customer exists in system (read-only if true)
  const [changeCustomerOpen, setChangeCustomerOpen] = useState(false);
  const [newCustomerEmail, setNewCustomerEmail] = useState("");
  const [changeCustomerLoading, setChangeCustomerLoading] = useState(false);
  const notify = useContext(NotificationContext);

  useEffect(() => {
    const fetchTaskDetails = async () => {
      try {
        const response = await fetch(envVariable.serverId + `/SCO/updateTask.php?TaskId=${taskId}`);
        const data = await response.json();
        if (data.status === "Success") {
          const task = data.data.task;

          // Parse UTC times from backend to local Date objects
          const appointmentDate = parseUTCDateTime(task.appointment_time);
          const calendarStartDate = parseUTCDateTime(task.calendar_start);
          const calendarEndDate = parseUTCDateTime(task.calendar_end);

          // Calculate expected duration properly
          const expectedDurationSecs = calendarStartDate && calendarEndDate
            ? Math.abs(calendarEndDate - calendarStartDate) / 1000
            : 0;

          setTaskData((prev) => ({
            ...prev,
            serviceInformation: task.service_info || "",
            notes: task.notes || "",
            faultyParts: task.faulty_parts || "",
            timer: secondsToHms((task.accumulated_secs || 0) + (task.is_active ? getElapsedSeconds(task.start_date_time) : 0)),
            // Display appointment time in local timezone (read-only) - with space instead of T
            originDate: appointmentDate ? formatLocalDateTimeDisplay(appointmentDate) : "",
            // Display assigned date (read-only legacy field) in local timezone
            assignedDate: calendarStartDate ? formatLocalDateTime(calendarStartDate).substring(0, 16) : "",
            // Editable calendar start/end times in local timezone
            calendarStart: calendarStartDate ? formatLocalDateTime(calendarStartDate) : "",
            calendarEnd: calendarEndDate ? formatLocalDateTime(calendarEndDate) : "",
            expectedDuration: secondsToHms(expectedDurationSecs),
            mechanicId: task.mechanic_id,
            originalMechanicId: task.mechanic_id, // Track original
            originalCalendarStart: calendarStartDate ? formatLocalDateTime(calendarStartDate) : "", // Track original
            originalCalendarEnd: calendarEndDate ? formatLocalDateTime(calendarEndDate) : "", // Track original
            status: task.status || "",
            customerId: task.Customer_id,
            customerName: task.Customer,
            time: task.accumulated_secs,
            vin_number: task.vin_number || "",
            accumulatedSeconds: task.accumulated_secs || 0,
            timerStartedAt: task.start_date_time || "",
            isTimerActive: Boolean(task.is_active),
          }));
          // Update the vin_number state with the fetched value
          setVinNumber(task.vin_number || "");
          setMechanics(data.data.mechanics || []);
          console.log("Vinnumber", task.vin_number);
          // Set appointment date and time from parsed appointment_time
          if (appointmentDate) {
            // Format: YYYY-MM-DD for date input
            setAppointmentDate(appointmentDate.toISOString().split('T')[0]);
            // Format: HH:MM for time input
            setAppointmentTime(appointmentDate.toTimeString().substring(0, 5));
          }

          // Set selected vehicle if task has one
          if (task.user_car_id) {
            setSelectedVehicleId(task.user_car_id);
          }

          // Fetch customer vehicles if we have a customer ID
          if (task.Customer_id) {
            fetchCustomerVehicles(task.Customer_id);
          }

          // Fetch availability and tasks data for validation
          if (task.sc_id) {
            fetchAvailabilityData(task.sc_id);
            fetchTasksData(task.sc_id);
          }
        }
      } catch (error) {
        console.error("Error fetching task details:", error);
      }
    };

    const fetchCustomerVehicles = async (customerId) => {
      try {
        const response = await fetch(
          envVariable.serverId + `/SCO/lookupCustomerByEmail.php?customer_id=${customerId}`
        );
        const data = await response.json();
        if (data.type === "success" && data.found) {
          // Store customer info
          if (data.customer) {
            setCustomerInfo({
              user_id: data.customer.user_id,
              first_name: data.customer.first_name || "",
              last_name: data.customer.last_name || "",
              email: data.customer.email || "",
              phone: data.customer.phone || "",
            });
            // Customer exists in system - mark as existing (read-only)
            setIsExistingCustomer(true);
          }
          // Store vehicles
          if (data.vehicles) {
            setCustomerVehicles(data.vehicles);
          }
        }
      } catch (error) {
        console.error("Error fetching customer vehicles:", error);
      }
    };

    const fetchAvailabilityData = async (scId) => {
      try {
        const response = await fetch(envVariable.serverId + `/mechanicSlots/getFreeslot.php?sc_id=${scId}`);
        const data = await response.json();
        if (data.type === "success") {
          setAvailability(data.message || []);
        }
      } catch (error) {
        console.error("Error fetching availability:", error);
      }
    };

    const fetchTasksData = async (scId) => {
      try {
        const response = await fetch(envVariable.serverId + `/SCO/Calendar.php?SC_Id=${scId}`);
        const data = await response.json();
        if (data.type === "success") {
          setAllTasks(data.tasks || []);
        }
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    };

    if (taskId) fetchTaskDetails();
  }, [taskId]);

  useEffect(() => {
    if (!taskData.isTimerActive || !taskData.timerStartedAt) {
      setTaskData((prev) => {
        const nextTimer = secondsToHms(prev.accumulatedSeconds || 0);
        return prev.timer === nextTimer ? prev : { ...prev, timer: nextTimer };
      });
      return undefined;
    }

    const updateTimer = () => {
      setTaskData((prev) => ({
        ...prev,
        timer: secondsToHms((prev.accumulatedSeconds || 0) + getElapsedSeconds(prev.timerStartedAt)),
      }));
    };

    updateTimer();
    const intervalId = setInterval(updateTimer, 1000);
    return () => clearInterval(intervalId);
  }, [taskData.isTimerActive, taskData.timerStartedAt, taskData.accumulatedSeconds]);

  // Handle Change Customer button click - looks up customer by email
  const handleChangeCustomer = async () => {
    const email = newCustomerEmail.trim();
    if (!email || !email.includes('@')) {
      notify("Please enter a valid email address", "error");
      return;
    }

    setChangeCustomerLoading(true);
    try {
      const response = await fetch(
        envVariable.serverId + `/SCO/lookupCustomerByEmail.php?email=${encodeURIComponent(email)}`
      );
      const data = await response.json();

      if (data.type === 'success' && data.found) {
        // Found existing customer - update all info (read-only mode)
        setCustomerInfo({
          user_id: data.customer.user_id,
          first_name: data.customer.first_name || '',
          last_name: data.customer.last_name || '',
          email: data.customer.email || '',
          phone: data.customer.phone || '',
        });
        // Update vehicles and reset selection
        setCustomerVehicles(data.vehicles || []);
        setSelectedVehicleId(''); // Reset vehicle selection for new customer
        setVinNumber('');
        // Update taskData customerId
        setTaskData(prev => ({ ...prev, customerId: data.customer.user_id }));
        // Mark as existing customer (read-only)
        setIsExistingCustomer(true);
        // Close dialog and reset input
        setChangeCustomerOpen(false);
        setNewCustomerEmail('');
        notify("Customer found and linked", "success");
      } else {
        // No customer found - allow creating new customer with this email (editable mode)
        setCustomerInfo({
          user_id: null, // Will be created when task is saved
          first_name: '',
          last_name: '',
          email: email,
          phone: '',
        });
        setCustomerVehicles([]);
        setSelectedVehicleId('');
        setVinNumber('');
        setTaskData(prev => ({ ...prev, customerId: null }));
        // Mark as new customer (editable)
        setIsExistingCustomer(false);
        // Close dialog and reset input
        setChangeCustomerOpen(false);
        setNewCustomerEmail('');
        notify("Email not found - you can enter new customer details", "info");
      }
    } catch (error) {
      console.error('Error looking up customer by email:', error);
      notify("Error looking up customer", "error");
    } finally {
      setChangeCustomerLoading(false);
    }
  };

  // Handle Remove Customer button click
  const handleRemoveCustomer = () => {
    if (!window.confirm('Are you sure you want to remove the customer from this task?')) {
      return;
    }
    setCustomerInfo({
      user_id: null,
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
    });
    setCustomerVehicles([]);
    setSelectedVehicleId('');
    setVinNumber('');
    setTaskData(prev => ({ ...prev, customerId: null }));
    setIsExistingCustomer(false); // Reset - no customer linked
    notify("Customer removed from task", "success");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTaskData({ ...taskData, [name]: value });
  };
  // eslint-disable-next-line no-unused-vars -- See above lines 39
  const handlePhoneNumberChange = (e) => {
    const { value } = e.target;
    setTaskData({ ...taskData, phoneNumber: value });

    // Validate phone number (must be 10 digits)
    const phonePattern = /^\d{10}$/;
    if (!phonePattern.test(value)) {
      setPhoneError("Phone number must be 10 digits.");
    } else {
      setPhoneError("");
    }
  };

  // Helper function to parse dates (same as Calendar.js toLocalDate)
  const toLocalDate = (val) => {
    if (!val) return new Date('Invalid');
    if (val instanceof Date) return new Date(val.getTime());
    if (typeof val === 'number') return new Date(val);
    if (typeof val === 'string') {
      const s = val.includes('T') ? val : val.replace(' ', 'T');
      const d = new Date(s);
      if (!isNaN(d.getTime())) return d;
      const p = Date.parse(val);
      return isNaN(p) ? new Date('Invalid') : new Date(p);
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? new Date('Invalid') : d;
  };

  // Validate mechanic availability (same logic as Calendar.js)
  const validateMechanicAvailability = (mechanicId, startDate, endDate, excludeTaskId = null) => {
    if (!mechanicId) return { valid: false, reason: "No mechanic assigned" };

    const taskStart = toLocalDate(startDate);
    const taskEnd = toLocalDate(endDate);
    if (isNaN(taskStart.getTime()) || isNaN(taskEnd.getTime())) {
      return { valid: false, reason: 'Invalid task time range' };
    }

    // Check relevant availability blocks
    const relevantAvailability = (availability || []).filter(a => {
      if (a.mechanic_id != mechanicId) return false;

      const isAvailable = a.mech_status === 'Available';
      const isOccupiedByCurrentTask = excludeTaskId && (a.task_id == excludeTaskId);

      if (!isAvailable && !isOccupiedByCurrentTask) return false;

      const availStart = toLocalDate(a.start_date_time);
      const availEnd = toLocalDate(a.end_date_time);
      if (isNaN(availStart.getTime()) || isNaN(availEnd.getTime())) return false;

      return availEnd > taskStart && availStart < taskEnd;
    });

    if (relevantAvailability.length === 0) {
      return { valid: false, reason: "Mechanic has no availability for this time" };
    }

    // Check for sick/vacation time
    const leaveOverlaps = (availability || []).some(a => {
      if (!(a.mech_status === 'Requested off' && a.mechanic_id == mechanicId)) return false;
      const ls = toLocalDate(a.start_date_time);
      const le = toLocalDate(a.end_date_time);
      if (isNaN(ls.getTime()) || isNaN(le.getTime())) return false;
      return le > taskStart && ls < taskEnd;
    });
    if (leaveOverlaps) {
      return { valid: false, reason: 'Mechanic has sick/vacation time during this slot' };
    }

    // Check if task is fully covered by the UNION of availability blocks
    // Sort blocks by start time and merge overlapping/adjacent ones
    const sortedBlocks = relevantAvailability
      .map(a => ({
        start: toLocalDate(a.start_date_time).getTime(),
        end: toLocalDate(a.end_date_time).getTime()
      }))
      .filter(b => !isNaN(b.start) && !isNaN(b.end))
      .sort((a, b) => a.start - b.start);

    // Merge overlapping/adjacent blocks
    const mergedBlocks = [];
    for (const block of sortedBlocks) {
      if (mergedBlocks.length === 0) {
        mergedBlocks.push({ ...block });
      } else {
        const last = mergedBlocks[mergedBlocks.length - 1];
        // If blocks overlap or are adjacent (within 1 second), merge them
        if (block.start <= last.end + 1000) {
          last.end = Math.max(last.end, block.end);
        } else {
          mergedBlocks.push({ ...block });
        }
      }
    }

    // Check if any merged block fully covers the task
    const taskStartMs = taskStart.getTime();
    const taskEndMs = taskEnd.getTime();
    const isFullyCovered = mergedBlocks.some(block =>
      taskStartMs >= block.start && taskEndMs <= block.end
    );

    if (!isFullyCovered) {
      return { valid: false, reason: "Mechanic is not available for the entire task duration" };
    }

    // Check for overlapping tasks
    const overlappingTasks = (allTasks || []).filter(task => {
      if (excludeTaskId != null && task.task_id == excludeTaskId) return false;
      if (task.mechanic_id != mechanicId) return false;

      // Use calendar_start/end if available, otherwise fall back to appointment_time
      let aptStart = toLocalDate(task.calendar_start);
      let aptEnd = toLocalDate(task.calendar_end);

      // If calendar_start is invalid, try appointment_time
      if (isNaN(aptStart.getTime())) {
        aptStart = toLocalDate(task.appointment_time);
      }

      // If calendar_end is invalid but we have a valid start, assume 30-minute duration
      if (isNaN(aptEnd.getTime()) && !isNaN(aptStart.getTime())) {
        aptEnd = new Date(aptStart.getTime() + 30 * 60 * 1000);
      }

      // Skip tasks that still don't have valid dates
      if (isNaN(aptStart.getTime()) || isNaN(aptEnd.getTime())) return false;

      return taskStart < aptEnd && taskEnd > aptStart;
    });

    if (overlappingTasks.length > 0) {
      return {
        valid: false,
        reason: `Mechanic already has a task scheduled during this time: ${overlappingTasks[0].service_info || 'Task'}`
      };
    }

    return { valid: true };
  };

  const handleMechanicChange = (e) => {
    const selectedMechanicId = e.target.value;
    const selectedMechanic = mechanics.find((mechanic) => mechanic.MechanicId.toString() === selectedMechanicId);

    setTaskData({
      ...taskData,
      mechanicId: selectedMechanicId,
      mechanic: selectedMechanic ? selectedMechanic.MechanicFirstName : "",
    });
  };

  const handleDateChange = (e) => {
    setAppointmentDate(e.target.value);
  };

  const handleTimeChange = (e) => {
    setAppointmentTime(e.target.value);
  };

  const handleCalendarStartChange = (e) => {
    const newStart = e.target.value;

    setTaskData(prev => {
      const updated = { ...prev, calendarStart: newStart };
      // Recalculate expected duration if both times are set
      if (newStart && prev.calendarEnd) {
        const startDate = new Date(newStart);
        const endDate = new Date(prev.calendarEnd);
        const durationSecs = Math.abs(endDate - startDate) / 1000;
        updated.expectedDuration = secondsToHms(durationSecs);
      }
      return updated;
    });
  };

  const handleCalendarEndChange = (e) => {
    const newEnd = e.target.value;

    setTaskData(prev => {
      const updated = { ...prev, calendarEnd: newEnd };
      // Recalculate expected duration if both times are set
      if (prev.calendarStart && newEnd) {
        const startDate = new Date(prev.calendarStart);
        const endDate = new Date(newEnd);
        const durationSecs = Math.abs(endDate - startDate) / 1000;
        updated.expectedDuration = secondsToHms(durationSecs);
      }
      return updated;
    });
  };

  // Handle vehicle selection change
  const handleVehicleChange = (e) => {
    const vehicleId = e.target.value;
    setSelectedVehicleId(vehicleId);

    // Update VIN number when vehicle is selected
    if (vehicleId) {
      const selectedVehicle = customerVehicles.find(v => v.user_car_id === vehicleId || v.user_car_id === parseInt(vehicleId));
      if (selectedVehicle) {
        setVinNumber(selectedVehicle.vin_number || "");
      }
    }
  };

  // Open edit vehicle form
  const openEditVehicleForm = () => {
    const selectedVehicle = customerVehicles.find(
      (v) => v.user_car_id === selectedVehicleId || v.user_car_id === parseInt(selectedVehicleId)
    );
    if (selectedVehicle) {
      setEditVehicle({
        user_car_id: selectedVehicle.user_car_id,
        car_name: selectedVehicle.car_name || "",
        make: selectedVehicle.make || "",
        model: selectedVehicle.model || "",
        year: selectedVehicle.year || "",
        submodel: selectedVehicle.submodel || "",
        vin_number: selectedVehicle.vin_number || "",
      });
      setEditVehicleOpen(true);
    }
  };

  // Handle edit vehicle submit
  const handleEditVehicle = async () => {
    if (!editVehicle.car_name.trim()) {
      notify("Vehicle name is required", "error");
      return;
    }

    try {
      const response = await fetch(
        envVariable.serverId + "/SCO/editCustomerVehicle.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(editVehicle),
        }
      );

      const result = await response.json();

      if (result.type === "success") {
        // Update the vehicle in the list
        setCustomerVehicles((prev) =>
          prev.map((v) =>
            v.user_car_id === editVehicle.user_car_id
              ? { ...v, ...result.vehicle }
              : v
          )
        );
        // Update VIN if the edited vehicle is currently selected
        if (selectedVehicleId === editVehicle.user_car_id || selectedVehicleId === String(editVehicle.user_car_id)) {
          setVinNumber(editVehicle.vin_number || "");
        }
        setEditVehicleOpen(false);
        notify("Vehicle updated successfully", "success");
      } else {
        notify(result.message || "Failed to update vehicle", "error");
      }
    } catch (error) {
      console.error("Error updating vehicle:", error);
      notify("Error updating vehicle", "error");
    }
  };

  // Handle add new vehicle
  const handleAddVehicle = async () => {
    if (!newVehicle.car_name.trim()) {
      notify("Vehicle name is required", "error");
      return;
    }

    if (!customerInfo.user_id) {
      notify("No customer linked to add vehicle", "error");
      return;
    }

    try {
      const response = await fetch(
        envVariable.serverId + "/SCO/addCustomerVehicle.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            user_id: customerInfo.user_id,
            ...newVehicle,
          }),
        }
      );

      const result = await response.json();

      if (result.type === "success") {
        // Add the new vehicle to the list
        const addedVehicle = result.vehicle || {
          user_car_id: result.user_car_id,
          ...newVehicle,
          display_name: `${newVehicle.year || ""} ${newVehicle.make || ""} ${newVehicle.model || ""}`.trim() || newVehicle.car_name,
        };
        setCustomerVehicles((prev) => [...prev, addedVehicle]);
        // Auto-select the new vehicle
        setSelectedVehicleId(result.user_car_id);
        setVinNumber(newVehicle.vin_number || "");
        // Reset form and close dialog
        setNewVehicle({
          car_name: "",
          make: "",
          model: "",
          year: "",
          submodel: "",
          engine: "",
          vin_number: "",
        });
        setAddVehicleOpen(false);
        notify(result.already_exists ? "Vehicle already exists, selected it" : "Vehicle added successfully", "success");
      } else {
        notify(result.message || "Failed to add vehicle", "error");
      }
    } catch (error) {
      console.error("Error adding vehicle:", error);
      notify("Error adding vehicle", "error");
    }
  };

  // Handle customer info changes
  const handleCustomerInfoChange = (field, value) => {
    setCustomerInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const totalSeconds = taskData.isTimerActive
      ? (taskData.accumulatedSeconds || 0) + getElapsedSeconds(taskData.timerStartedAt)
      : (taskData.accumulatedSeconds || 0);

    // Check if mechanic or times have changed from original values
    const mechanicChanged = taskData.mechanicId != taskData.originalMechanicId;
    const timesChanged = taskData.calendarStart !== taskData.originalCalendarStart ||
                         taskData.calendarEnd !== taskData.originalCalendarEnd;

    // Only validate mechanic availability if mechanic or times have changed
    // If nothing changed, the task already occupies this slot (even if mech_availability doesn't reflect it)
    if (taskData.mechanicId && taskData.calendarStart && taskData.calendarEnd && (mechanicChanged || timesChanged)) {
      const validation = validateMechanicAvailability(
        taskData.mechanicId,
        taskData.calendarStart,
        taskData.calendarEnd,
        taskId
      );

      if (!validation.valid) {
        notify(`Cannot update task: ${validation.reason}`, "error");
        return; // Don't clear fields, just show error and return
      }
    }

    // Convert local calendar times back to UTC for backend storage
    const calendarStartUTC = taskData.calendarStart ? formatUTCDateTime(new Date(taskData.calendarStart)) : null;
    const calendarEndUTC = taskData.calendarEnd ? formatUTCDateTime(new Date(taskData.calendarEnd)) : null;

    // Convert appointment date/time to UTC for backend storage
    // Combine date and time into a single datetime, then convert to UTC
    let appointmentTimeUTC = null;
    if (appointmentDate && appointmentTime) {
      const appointmentLocal = new Date(`${appointmentDate}T${appointmentTime}`);
      appointmentTimeUTC = formatUTCDateTime(appointmentLocal);
    }

    try {
      // Update task
      const updateResponse = await fetch(envVariable.serverId + `/SCO/updateTask.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          TaskId: taskId,
          serviceInformation: taskData.serviceInformation,
          notes: taskData.notes,
          faulty_parts: taskData.faultyParts,
          status: taskData.status,
          mechanicId: parseInt(taskData.mechanicId),
          customerId: taskData.customerId,
          calendar_start: calendarStartUTC,
          calendar_end: calendarEndUTC,
          appointment_time: appointmentTimeUTC,
          vin_number: vin_number,
          user_car_id: selectedVehicleId || null,
          timer: taskData.timer,
          accumulated_secs: totalSeconds,
        }),
      });
      const updateData = await updateResponse.json();

      // Only update/create customer info if it's a NEW customer (not existing in system)
      // SCO should not be able to edit existing customer data
      if (!isExistingCustomer && customerInfo.email) {
        try {
          // For new customers, we need to create them first or update if they have a user_id
          await fetch(envVariable.serverId + `/SCO/updateCustomer.php`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              user_id: customerInfo.user_id,
              first_name: customerInfo.first_name,
              last_name: customerInfo.last_name,
              email: customerInfo.email,
              phone: customerInfo.phone,
            }),
          });
        } catch (customerError) {
          console.error("Error updating customer:", customerError);
          // Don't block task update if customer update fails
        }
      }

      if (updateData.status === "Success") {
        console.log("Task updated successfully:", updateData.message);
        notify("Task updated successfully", "success");
        navigate("/tasks");
      } else {
        console.error("Failed to update task:", updateData.message);
        notify(updateData.message,"error");
      }
    } catch (error) {
      console.error("Error submitting the update:", error);
      notify("An error occurred while updating the task. Please try again.","error");
    }
  };

  const handleBack = () => navigate(-1);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this task? This action cannot be undone.')) {
      return;
    }

    try {
      const user_id = localStorage.getItem("User_Id");
      const sc_id = localStorage.getItem("SC_Id");

      const response = await fetch(envVariable.serverId + "/SCO/deleteTask.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          task_id: taskId,
          deleted_by: user_id,
          sc_id: sc_id,
        }),
      });

      const result = await response.json();

      if (result.type === 'success') {
        notify('Task permanently deleted', 'success');
        navigate("/tasks");
      } else {
        notify('Failed to delete task: ' + result.message, 'error');
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      notify('An error occurred while deleting the task', 'error');
    }
  };

  return (
    <>
      <Header />
      <Box
        className="edit-task-container"
        sx={{
          backgroundColor: "#f4f7f6",
          borderRadius: "8px",
          boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
          padding: "50px",
          maxWidth: "93vw",
          margin: "auto",
          marginTop: "3vw",
          minHeight: "80vh",
        }}
      >
        <Typography variant="h4" component="h2" align="center" gutterBottom>
          Task Information
        </Typography>
        {taskId && (
          <Typography variant="subtitle1" align="center" gutterBottom sx={{ color: "text.secondary", mb: 2 }}>
            Task ID: {taskId}
          </Typography>
        )}
        <Box component="form" onSubmit={handleSubmit} sx={{ padding: 3 }}>
          <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label="Service Information"
              name="serviceInformation"
              value={taskData.serviceInformation}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              multiline
              minRows={3}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Notes"
              name="notes"
              value={taskData.notes}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              multiline
              minRows={3}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Faulty Parts"
              name="faultyParts"
              value={taskData.faultyParts}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              multiline
              minRows={3}
            />
          </Grid>


            {/* Customer Info Section - always show with email and action buttons */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "text.secondary", mb: 1 }}>
                Customer Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <TextField
                  label="Customer Email"
                  value={customerInfo.email || ""}
                  fullWidth
                  variant="outlined"
                  type="email"
                  disabled
                  helperText={customerInfo.user_id ? `${customerInfo.first_name} ${customerInfo.last_name}`.trim() || "Customer linked" : "No customer linked"}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setChangeCustomerOpen(true)}
                  sx={{ height: "56px", minWidth: "100px" }}
                >
                  {customerInfo.user_id ? "Change" : "Add"}
                </Button>
                {customerInfo.user_id && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleRemoveCustomer}
                    sx={{ height: "56px", minWidth: "100px" }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            </Grid>
            {/* Show customer details - read-only for existing customers, editable for new */}
            {(customerInfo.user_id || customerInfo.email) && (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="First Name"
                    value={customerInfo.first_name}
                    onChange={(e) => handleCustomerInfoChange("first_name", e.target.value)}
                    fullWidth
                    variant="outlined"
                    disabled={isExistingCustomer}
                    helperText={isExistingCustomer ? "Cannot edit existing customer" : ""}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Last Name"
                    value={customerInfo.last_name}
                    onChange={(e) => handleCustomerInfoChange("last_name", e.target.value)}
                    fullWidth
                    variant="outlined"
                    disabled={isExistingCustomer}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Phone"
                    value={customerInfo.phone}
                    onChange={(e) => handleCustomerInfoChange("phone", e.target.value)}
                    fullWidth
                    variant="outlined"
                    disabled={isExistingCustomer}
                  />
                </Grid>
              </>
            )}

            {/* Vehicle Selection */}
            <Grid item xs={8} sm={6}>
              {customerVehicles.length > 0 || customerInfo.user_id || customerInfo.email ? (
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <TextField
                    select
                    label="Vehicle"
                    name="vehicle"
                    value={selectedVehicleId}
                    onChange={handleVehicleChange}
                    fullWidth
                    variant="outlined"
                    helperText={isExistingCustomer && customerVehicles.length > 0 ? "Select from existing vehicles" : ""}
                  >
                    <MenuItem value="">
                      <em>Select a vehicle</em>
                    </MenuItem>
                    {customerVehicles.map((vehicle) => (
                      <MenuItem key={vehicle.user_car_id} value={vehicle.user_car_id}>
                        {vehicle.display_name || `${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}`.trim() || vehicle.car_name || "Unnamed Vehicle"}
                      </MenuItem>
                    ))}
                  </TextField>
                  {/* Only show Edit button for new customers (not in system) */}
                  {selectedVehicleId && !isExistingCustomer && (
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={openEditVehicleForm}
                      sx={{ height: "56px", minWidth: "80px" }}
                    >
                      Edit
                    </Button>
                  )}
                  {/* Only show Add button for new customers (not in system) */}
                  {(customerInfo.user_id || customerInfo.email) && !isExistingCustomer && (
                    <Button
                      variant="outlined"
                      color="success"
                      onClick={() => setAddVehicleOpen(true)}
                      sx={{ height: "56px", minWidth: "80px" }}
                    >
                      Add
                    </Button>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No customer linked to this task
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Appointment"
                name="originDate"
                value={taskData.originDate}
                disabled
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Calendar Start"
                name="calendarStart"
                type="datetime-local"
                value={taskData.calendarStart}
                onChange={handleCalendarStartChange}
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Calendar End"
                name="calendarEnd"
                type="datetime-local"
                value={taskData.calendarEnd}
                onChange={handleCalendarEndChange}
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Expected Duration"
                name="expectedDuration"
                value={taskData.expectedDuration}
                disabled
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Actual Time Taken"
                name="timer"
                value={taskData.timer}
                fullWidth
                variant="outlined"
                InputProps={{ readOnly: true }}
                helperText={taskData.isTimerActive ? "Running while task is in progress" : "Tracked automatically from task status"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Mechanic"
                name="mechanic"
                value={taskData.mechanicId}
                onChange={handleMechanicChange}
                fullWidth
                variant="outlined"
              >
                <MenuItem value="">
                  <em>Select a mechanic</em>
                </MenuItem>
                {mechanics.map((mechanic) => (
                  <MenuItem key={mechanic.MechanicId} value={mechanic.MechanicId}>
                    {mechanic.MechanicFirstName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Status"
                name="status"
                value={taskData.status}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
              >
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="In-Progress">In-Progress</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Reschedule Appointment Date"
                type="date"
                value={appointmentDate}
                onChange={handleDateChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Reschedule Appointment Time"
                type="time"
                value={appointmentTime}
                onChange={handleTimeChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="center" gap={2}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleBack}
                sx={{ backgroundColor: "grey.500", color: "white", minWidth: "15vw" }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                sx={{ minWidth: "15vw" }}
              >
                Delete Task
              </Button>
              <Button type="submit" variant="contained" color="primary" sx={{ minWidth: "15vw" }}>
                Submit
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Edit Vehicle Dialog */}
      <Dialog open={editVehicleOpen} onClose={() => setEditVehicleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Vehicle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: 1 }}>
            <TextField
              label="Vehicle Name *"
              value={editVehicle.car_name}
              onChange={(e) => setEditVehicle({ ...editVehicle, car_name: e.target.value })}
              placeholder="e.g., My Daily Driver, Work Truck"
              required
              error={!editVehicle.car_name.trim()}
              helperText={!editVehicle.car_name.trim() ? "Vehicle name is required" : ""}
            />
            <TextField
              label="Year"
              value={editVehicle.year}
              onChange={(e) => setEditVehicle({ ...editVehicle, year: e.target.value })}
              placeholder="e.g., 2020"
            />
            <TextField
              label="Make"
              value={editVehicle.make}
              onChange={(e) => setEditVehicle({ ...editVehicle, make: e.target.value })}
              placeholder="e.g., Toyota"
            />
            <TextField
              label="Model"
              value={editVehicle.model}
              onChange={(e) => setEditVehicle({ ...editVehicle, model: e.target.value })}
              placeholder="e.g., Camry"
            />
            <TextField
              label="Submodel/Trim (Optional)"
              value={editVehicle.submodel}
              onChange={(e) => setEditVehicle({ ...editVehicle, submodel: e.target.value })}
              placeholder="e.g., SE, XLE"
            />
            <TextField
              label="VIN Number (Optional)"
              value={editVehicle.vin_number}
              onChange={(e) => setEditVehicle({ ...editVehicle, vin_number: e.target.value })}
              placeholder="17-character VIN"
              inputProps={{ maxLength: 17 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditVehicleOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleEditVehicle}
            disabled={!editVehicle.car_name.trim()}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Vehicle Dialog */}
      <Dialog open={addVehicleOpen} onClose={() => setAddVehicleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Vehicle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: 1 }}>
            <TextField
              label="Vehicle Name *"
              value={newVehicle.car_name}
              onChange={(e) => setNewVehicle({ ...newVehicle, car_name: e.target.value })}
              placeholder="e.g., My Daily Driver, Work Truck"
              required
              error={!newVehicle.car_name.trim()}
              helperText={!newVehicle.car_name.trim() ? "Vehicle name is required" : ""}
            />
            <TextField
              label="Year"
              value={newVehicle.year}
              onChange={(e) => setNewVehicle({ ...newVehicle, year: e.target.value })}
              placeholder="e.g., 2020"
            />
            <TextField
              label="Make"
              value={newVehicle.make}
              onChange={(e) => setNewVehicle({ ...newVehicle, make: e.target.value })}
              placeholder="e.g., Toyota"
            />
            <TextField
              label="Model"
              value={newVehicle.model}
              onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })}
              placeholder="e.g., Camry"
            />
            <TextField
              label="Submodel/Trim (Optional)"
              value={newVehicle.submodel}
              onChange={(e) => setNewVehicle({ ...newVehicle, submodel: e.target.value })}
              placeholder="e.g., SE, XLE"
            />
            <TextField
              label="Engine (Optional)"
              value={newVehicle.engine}
              onChange={(e) => setNewVehicle({ ...newVehicle, engine: e.target.value })}
              placeholder="e.g., 2.5L 4-cyl"
            />
            <TextField
              label="VIN Number (Optional)"
              value={newVehicle.vin_number}
              onChange={(e) => setNewVehicle({ ...newVehicle, vin_number: e.target.value })}
              placeholder="17-character VIN"
              inputProps={{ maxLength: 17 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddVehicleOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleAddVehicle}
            disabled={!newVehicle.car_name.trim()}
          >
            Add Vehicle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Customer Dialog */}
      <Dialog open={changeCustomerOpen} onClose={() => setChangeCustomerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{customerInfo.user_id ? "Change Customer" : "Add Customer"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Enter the email address of the customer you want to link to this task.
            </Typography>
            <TextField
              label="Customer Email"
              value={newCustomerEmail}
              onChange={(e) => setNewCustomerEmail(e.target.value)}
              fullWidth
              variant="outlined"
              type="email"
              placeholder="customer@example.com"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleChangeCustomer();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setChangeCustomerOpen(false); setNewCustomerEmail(''); }}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleChangeCustomer}
            disabled={changeCustomerLoading || !newCustomerEmail.trim()}
          >
            {changeCustomerLoading ? "Searching..." : "Find Customer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </>
  );
};

export default EditTask;
